import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { MissingCustomerState, CreateCustomerData } from "@models/missing-customer.model";
import i18next from "i18next";
import { toast } from "keep-react";
import { setUserSuccess } from "./userSlice";
import { logger } from "@/lib/utils/logger";

const initialState: MissingCustomerState = {
  isModalOpen: false,
  isSubmitting: false,
  isSuccess: false,
  error: null,
  status: "idle",
};

// Async thunk für das Erstellen des Kundenprofils
export const createCustomerProfile = createAsyncThunk(
  "missingCustomer/createCustomerProfile",
  async (customerData: CreateCustomerData, { rejectWithValue, dispatch, getState }) => {
    try {
      // Aktuelle User-ID aus dem Auth-State holen
      const state = getState() as { auth: { userId: string } };
      const userId = state.auth.userId;
      
      if (!userId) {
        return rejectWithValue("Benutzer ist nicht eingeloggt");
      }
      
      // Token aus dem localStorage holen
      const token = localStorage.getItem("auth_token");
      
      if (!token) {
        return rejectWithValue("Kein Auth-Token gefunden");
      }
      
      // Step 3 aus createCustomerApi: Create the customer profile
      // Wir verwenden hier direkt fetch anstelle von fetchWithBQ
      const response = await fetch('/api/data/kunden', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          where: {
            users: userId
          },
          fields: {
            anrede: customerData.anrede,
            vorname: customerData.vorname,
            nachname: customerData.nachname,
            e_mail: customerData.email,
            telefon: customerData.telefon || "",
            strasse_und_hausnummer: `${customerData.strasse} ${customerData.hausnummer}`,
            adresszusatz: customerData.adresszusatz || "",
            plz: customerData.plz,
            ort: customerData.ort,
            country: customerData.country,
            geburtsdatum: customerData.geburtsdatum,
            teilnahmebedingungen: 1,
            datenschutz: 1,
            marketing: customerData.marketing,
            users: userId,
            erstelldatum: new Date().toISOString(),
            trigger_create_card: customerData.trigger_create_card,
            brand_create_card: customerData.brand_create_card,
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData?.message || i18next.t("errors.general"));
      }
      
      // Erfolgreiche Antwort verarbeiten
      const responseData = await response.json();
      
      // Erfolg!
      dispatch(setUserSuccess());
      return responseData;
    } catch (error) {
      logger.error("Fehler bei der Kundenerstellung", error);
      return rejectWithValue(i18next.t("errors.network"));
    }
  }
);

export const missingCustomerSlice = createSlice({
  name: "missingCustomer",
  initialState,
  reducers: {
    // Öffnet das Modal für die Kundenerstellung
    openMissingCustomerModal: (state) => {
      state.isModalOpen = true;
      state.error = null;
    },
    // Schließt das Modal für die Kundenerstellung
    closeMissingCustomerModal: (state) => {
      state.isModalOpen = false;
      state.error = null;
    },
    // Toggle-Funktion für das Modal
    toggleMissingCustomerModal: (state) => {
      state.isModalOpen = !state.isModalOpen;
      state.error = null;
    },
    // Setzt den Status auf den Initialzustand zurück
    resetMissingCustomerState: () => initialState,
    // Setzt den Erfolgsstatus zurück
    clearMissingCustomerSuccess: (state) => {
      state.isSuccess = false;
    },
    // Setzt den Fehlerstatus zurück
    clearMissingCustomerError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handling für createCustomerProfile
      .addCase(createCustomerProfile.pending, (state) => {
        state.status = "loading";
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createCustomerProfile.fulfilled, (state) => {
        state.status = "succeeded";
        state.isSubmitting = false;
        state.isSuccess = true;
        state.isModalOpen = false;
        
        toast.success(i18next.t("missingCustomer.success"), {
          duration: 5000,
        });
      })
      .addCase(createCustomerProfile.rejected, (state, action) => {
        state.status = "failed";
        state.isSubmitting = false;
        state.error = action.payload as string || i18next.t("errors.general");
        
        toast.error(i18next.t("missingCustomer.error"), {
          description: state.error,
          duration: 5000,
        });
      });
  },
});

export const {
  openMissingCustomerModal,
  closeMissingCustomerModal,
  toggleMissingCustomerModal,
  resetMissingCustomerState,
  clearMissingCustomerSuccess,
  clearMissingCustomerError,
} = missingCustomerSlice.actions;

export default missingCustomerSlice.reducer;
